import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetArticleByIdQuery } from '../../hooks/queries/useGetArticleByIdQuery';
import { renderToReactElement } from '@tiptap/static-renderer';
import { GhostLoader } from '../../components/GhostLoader';
import classes from './ArticlePage.module.css';
import { Alert } from '../../components/Alert/Alert';
import { Paragraph } from '../../components/Paragraph';
import { BaseKit } from 'reactjs-tiptap-editor';
import { Blockquote } from 'reactjs-tiptap-editor/blockquote';
import { Bold } from 'reactjs-tiptap-editor/bold';
import { BulletList } from 'reactjs-tiptap-editor/bulletlist';
import { Clear } from 'reactjs-tiptap-editor/clear';
import { Code } from 'reactjs-tiptap-editor/code';
import { CodeBlock } from 'reactjs-tiptap-editor/codeblock';
import { Color } from 'reactjs-tiptap-editor/color';
import { Heading } from 'reactjs-tiptap-editor/heading';
import { History } from 'reactjs-tiptap-editor/history';
import { HorizontalRule } from 'reactjs-tiptap-editor/horizontalrule';
import { Image } from 'reactjs-tiptap-editor/image';
import { Italic } from 'reactjs-tiptap-editor/italic';
import { Link } from 'reactjs-tiptap-editor/link';
import { MoreMark } from 'reactjs-tiptap-editor/moremark';
import { OrderedList } from 'reactjs-tiptap-editor/orderedlist';
import { Strike } from 'reactjs-tiptap-editor/strike';
import { TextAlign } from 'reactjs-tiptap-editor/textalign';
import { TextUnderline } from 'reactjs-tiptap-editor/textunderline';
import { Button } from '../../components/Button';

const extensions: any = [
  BaseKit.configure({
    placeholder: {
      showOnlyCurrent: true,
      placeholder: 'Skriv inn tekst her',
    },
    characterCount: false,
  }),
  History,
  Clear,
  Heading.configure({ spacer: true }),
  Bold,
  Italic,
  TextUnderline,
  Strike,
  MoreMark,
  Color.configure({ spacer: true }),
  BulletList,
  OrderedList,
  TextAlign.configure({ types: ['heading', 'paragraph'], spacer: true }),
  Link,
  Image,
  Blockquote,
  HorizontalRule,
  Code.configure({
    toolbar: false,
  }),
  CodeBlock,
];

export const ArticlePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const numericId = Number(id);
  const isValidId = Number.isFinite(numericId) && numericId > 0;

  const {
    data: articleById,
    isLoading: isArticleByIdLoading,
    error: articleByIdError,
  } = useGetArticleByIdQuery(Number(id), isValidId);

  if (!isValidId) {
    return (
      <div className={classes.container}>
        <Alert title={t('articlePage.invalidArticleId')} variant="danger" size="lg">
          <Paragraph size="md">Kunne ikke lese artikkel-ID fra URL.</Paragraph>
        </Alert>
      </div>
    );
  }

  if (isArticleByIdLoading) {
    return (
      <div className={classes.container}>
        <GhostLoader
          aria-busy="true"
          style={{ width: '100%', maxWidth: 580, height: 80 }}
        />
        <div className={classes.loaderCards}>
          <GhostLoader
            aria-busy="true"
            style={{ width: '100%', maxWidth: 580, height: 80 }}
          />
          <GhostLoader
            aria-busy="true"
            style={{ width: '100%', height: 300 }}
          />
          <GhostLoader
            aria-busy="true"
            style={{ width: '100%', maxWidth: 580, height: 80 }}
          />
          <GhostLoader aria-busy="true" style={{ width: 300, height: 150 }} />
        </div>
      </div>
    );
  }

  if (articleByIdError) {
    return (
      <div className={classes.container}>
        <Alert title={articleByIdError.name} variant="danger" size="lg">
          <Paragraph size="md">{articleByIdError.message}</Paragraph>
        </Alert>
      </div>
    );
  }

  if (!articleById) {
    return null;
  }

  return (
    <div className={classes.container}>
      <Button
        type="button"
        variant="secondary"
        size="medium"
        onClick={() => {
          navigate('/content-system/articles');
        }}
        className={classes.publishButton}
      >
        Tilbake til oversikt
      </Button>
      <h1 className={classes.articleTitle}>{articleById.title}</h1>

      <img
        className={classes.articleCoverImage}
        src={articleById.coverImageUrl}
        alt={articleById.coverImageAlt ?? articleById.title}
      />

      <div className={classes.articleContent}>
        {articleById.jsonContent &&
          renderToReactElement({
            content: articleById.jsonContent,
            extensions,
          })}
      </div>
    </div>
  );
};
